// Pool of pairing-reason strings used for the post-add-to-cart recommendation card.
// A specific (added product, recommended product) pair always shows the same reason
// (picked deterministically via a hash of the two product ids, see pickReason below),
// but which reason a given pair gets is effectively unpredictable across a whole
// catalog without checking every combination by hand.
const CART_RECOMMENDATION_REASONS = [
  'These two are a customer favorite combo.',
  'Pairs beautifully with what you just grabbed.',
  'People usually reach for this right after.',
  'A little extra love for your routine.',
  'Goes hand-in-hand with your pick.',
  'Frequently added together by other shoppers.',
  'This one rounds out the routine nicely.',
  'A natural next step in your self-care lineup.',
  'Customers who bought this often add it too.',
  'Works wonderfully alongside your selection.',
  'The perfect finishing touch for your order.',
  'This tends to disappear from carts together with your pick.',
  'A cozy companion to what’s already in your bag.',
  'Loved by shoppers who picked the same item.',
  'Rounds out your skincare shelf just right.',
  'Often grabbed as the second half of the set.',
  'A small addition that makes a big difference.',
  'This one’s a quiet favorite among repeat customers.',
  'Complements your choice from top to bottom.',
  'A popular pick for anyone building a routine.',
  'Many customers treat these as a package deal.',
  'This tends to get scooped up in the same order.',
  'Adds a little something extra to your self-care day.',
  'A gentle nudge toward trying something new.',
  'This one keeps showing up in the same cart.',
  'Worth a peek before you check out.',
  'A cult favorite that pairs well with almost everything.',
  'Some of our happiest customers order these together.',
  'This one’s often the “just one more thing” add.',
  'A nice way to stretch your self-care routine a little further.',
];

// Deterministic string hash (djb2-ish) so the same pair of product ids always
// produces the same index into CART_RECOMMENDATION_REASONS, without ever hardcoding
// a reason to a specific pair.
function hashProductPair(idA, idB) {
  const str = String(idA) + '|' + String(idB);
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function pickCartRecommendationReason(addedProductId, recommendedProductId) {
  const index = hashProductPair(addedProductId, recommendedProductId) % CART_RECOMMENDATION_REASONS.length;
  return CART_RECOMMENDATION_REASONS[index];
}

function cartNotificationEscapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str || '';
  return div.innerHTML;
}

function cartNotificationGetImageUrl(product) {
  if (!product) return '';
  if (typeof product.featured_image === 'string') return product.featured_image;
  if (product.featured_image && product.featured_image.src) return product.featured_image.src;
  if (Array.isArray(product.images) && product.images.length) {
    const first = product.images[0];
    return typeof first === 'string' ? first : (first && first.src) || '';
  }
  return '';
}

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    this.addEventListener('click', this.handleRecommendationAddClick.bind(this));
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
    this.loadRecommendation();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  // Fetches a single recommended product for whatever was just added to the cart
  // (via Shopify's public Product Recommendations endpoint) and renders a small
  // card with a deterministic, human-sounding pairing reason.
  loadRecommendation() {
    const container = this.querySelector('.cart-notification-recommendation');
    if (!container) return;

    const productId = container.dataset.productId;
    if (!productId) return;

    container.innerHTML = '';
    container.classList.remove('is-visible');

    fetch(`/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=1`)
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then((data) => {
        const recommended = data && data.products && data.products[0];
        if (!recommended) return;

        const reason = pickCartRecommendationReason(productId, recommended.id);
        const imageUrl = cartNotificationGetImageUrl(recommended);
        const variant =
          (recommended.variants && recommended.variants[0]) || { id: recommended.id };

        container.innerHTML = `
          <p class="cart-notification-recommendation__reason">${cartNotificationEscapeHtml(reason)}</p>
          <div class="cart-notification-recommendation__card">
            ${
              imageUrl
                ? `<a href="${recommended.url}" class="cart-notification-recommendation__image-link" tabindex="-1">
                    <img src="${imageUrl}" alt="${cartNotificationEscapeHtml(recommended.title)}" width="56" height="56" loading="lazy">
                  </a>`
                : ''
            }
            <div class="cart-notification-recommendation__info">
              <a href="${recommended.url}" class="cart-notification-recommendation__title">${cartNotificationEscapeHtml(
                recommended.title
              )}</a>
            </div>
            <button
              type="button"
              class="cart-notification-recommendation__add-button link"
              data-variant-id="${variant.id}"
            >
              ${window.variantStrings && window.variantStrings.addToCart ? window.variantStrings.addToCart : 'Add'}
            </button>
          </div>
        `;
        container.classList.add('is-visible');
      })
      .catch(() => {
        container.innerHTML = '';
        container.classList.remove('is-visible');
      });
  }

  handleRecommendationAddClick(evt) {
    const button = evt.target.closest('.cart-notification-recommendation__add-button');
    if (!button) return;
    evt.preventDefault();
    this.addRecommendedProductToCart(button);
  }

  addRecommendedProductToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = '...';

    fetch(`${routes.cart_add_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          button.disabled = false;
          button.textContent = originalText;
          return;
        }

        button.textContent = window.accessibilityStrings && window.accessibilityStrings.added ? window.accessibilityStrings.added : 'Added';

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-notification-recommendation',
            productVariantId: variantId,
            cartData: response,
          });
        }
      })
      .catch((e) => {
        console.error(e);
        button.disabled = false;
        button.textContent = originalText;
      });
  }
}

customElements.define('cart-notification', CartNotification);
